import type { Agent, AgentReadCursors, Persisted } from '@tunasong/schemas'
import { useUserConfig } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, MouseEvent } from 'react'
import { useCallback, useEffect } from 'react'
import { AgentRailCard } from './agent-rail-card.js'
import styles from './agent-rail.module.css'
import { computeClearedCursors } from './clear-agent-unread.js'
import { useAgentChildren } from './use-agent-children.js'

export interface AgentRailProps {
  /** Host entity id (song / page / folder) whose agent children populate the rail. */
  parentId?: string | null
  /** Id of the currently-open agent panel; drives the active-state styling. */
  activeAgentId?: string | null
  /**
   * Called when the user clicks an agent card. Typically opens the chat
   * side-panel; that panel is the next UX sub-task. Until it lands, the
   * caller may fall back to navigating to the agent entity.
   */
  onSelect: (agent: Persisted<Agent>, event: MouseEvent<HTMLButtonElement>) => void
  className?: string
}

/**
 * Right-gutter strip showing one avatar card per agent attached to the host.
 * Renders nothing when the host has no agent children — per Joni's empty-state
 * spec, the rail is hidden and "Attach agent…" lives in the insert menu /
 * header overflow.
 *
 * Consumes the three plumbing hooks landed alongside this:
 *  - useAgentChildren — cache-driven list of agent children.
 *  - useAgentRuntimeStatus (inside AgentRailCard) — live status with 90s TTL.
 *  - deriveAgentUnread (inside AgentRailCard) — derived from read cursors.
 *
 * Lazy-initializes a read cursor on first render for any agent the user has
 * never seen before. This suppresses a noisy "everything unread" state on
 * first attachment without requiring the user to click each agent once.
 */
export const AgentRail: FC<AgentRailProps> = ({ parentId, activeAgentId, onSelect, className }) => {
  const agents = useAgentChildren(parentId)
  const [cursors, updateCursors] = useUserConfig<'agentReadCursors'>('agentReadCursors')
  const cursorMap = cursors as AgentReadCursors | undefined

  // Lazy first-visit initialization: for any agent without a cursor, stamp
  // `now` so the dot doesn't flash for already-old activity. One write batches
  // every uninitialized agent to keep profile writes infrequent.
  useEffect(() => {
    if (agents.length === 0) {return}
    const missing = agents.filter(agent => !cursorMap?.[agent.id])
    if (missing.length === 0) {return}
    let next = cursorMap
    for (const agent of missing) {
      next = computeClearedCursors(agent.id, next)
    }
    updateCursors(next as never)
  }, [agents, cursorMap, updateCursors])

  const handleSelect = useCallback(
    (agent: Persisted<Agent>, event: MouseEvent<HTMLButtonElement>) => {
      // Bump the cursor for this agent on click — the user is acknowledging
      // the activity by opening the conversation. The bell consolidation
      // sub-task will share this same write path on nudge dismiss.
      updateCursors(computeClearedCursors(agent.id, cursorMap) as never)
      onSelect(agent, event)
    },
    [cursorMap, onSelect, updateCursors]
  )

  if (agents.length === 0) {
    return null
  }

  // <nav> + <ul> rather than role="toolbar": ARIA APG requires toolbar to
  // implement roving tabindex (arrow keys, Home/End), which we don't —
  // Tab-between-items matches Jakob's law for sidebar lists and is the right
  // contract until the side-panel sub-task introduces real keyboard nav.
  return (
    <nav className={cn(styles.rail, className)} aria-label="Agents">
      <ul className={styles.list}>
        {agents.map(agent => (
          <li key={agent.id}>
            <AgentRailCard
              agent={agent}
              cursor={cursorMap?.[agent.id]}
              active={activeAgentId === agent.id}
              onClick={handleSelect}
            />
          </li>
        ))}
      </ul>
    </nav>
  )
}
